import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle, Empty } from "@/components/GlassBits";
import { formatINR, formatDate, statusBadgeClass } from "@/lib/format";
import { Copy, ExternalLink, Search, X, Plus } from "lucide-react";
import { sounds } from "@/lib/sound";

const CURRENCIES = ["INR", "USD", "EUR", "GBP"];

export default function PaymentLinks() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", amount: "", purpose: "", branch: "",
    message: "", notes: "", expiry_days: 7, partial_payment: false, logo_link: "",
    reference_id: "", currency: "INR",
  });

  const load = useCallback(async () => {
    const p = { params: {} };
    if (q) p.params.q = q;
    if (status) p.params.status = status;
    try {
      const { data } = await api.get("/admin/payment-links", p);
      setItems(data);
    } catch (e) {
      console.error("[PaymentLinks] load failed", e);
    }
  }, [q, status]);

  const loadBranches = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/branches");
      setBranches(data);
    } catch (e) {
      console.error("[PaymentLinks] loadBranches failed", e);
    }
  }, []);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        expiry_days: parseInt(form.expiry_days) || 7,
      };
      const { data } = await api.post("/admin/payment-links", payload);
      toast.success(`Invoice ${data.invoice_no} created`);
      sounds.linkCreated();
      setOpen(false);
      setForm({
        customer_name: "", email: "", phone: "", amount: "", purpose: "", branch: "",
        message: "", notes: "", expiry_days: 7, partial_payment: false, logo_link: "",
        reference_id: "", currency: "INR",
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create link");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div>
      <SectionTitle
        eyebrow="Ledger"
        title="Payment Links"
        right={
          <button onClick={() => setOpen(true)} className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="pl-new-btn">
            <Plus size={16} /> New link
          </button>
        }
      />

      <div className="ldc-glass p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by invoice, email, phone, customer…"
            className="ldc-input w-full pl-9 pr-3 py-2 rounded-md text-sm"
            data-testid="pl-search"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="ldc-input rounded-md px-3 py-2 text-sm" data-testid="pl-status-filter">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="expired">Expired</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="ldc-glass overflow-hidden">
        <div className="overflow-x-auto ldc-scroll">
          <table className="w-full ldc-table" data-testid="pl-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Branch / Purpose</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-white/40">No payment links yet.</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.invoice_no} data-testid={`pl-row-${it.invoice_no}`}>
                  <td className="font-mono text-[#fbce18]">{it.invoice_no}</td>
                  <td>
                    <div className="text-white/90">{it.customer_name}</div>
                    <div className="text-xs text-white/40">{it.email}</div>
                  </td>
                  <td>
                    <div className="text-white/80">{it.branch}</div>
                    <div className="text-xs text-white/40">{it.purpose}</div>
                  </td>
                  <td className="font-medium">{formatINR(it.amount, it.currency)}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status}</span></td>
                  <td className="text-white/50 text-xs">{formatDate(it.created_at)}</td>
                  <td>
                    <div className="flex gap-1">
                      {it.payment_link && (
                        <>
                          <button title="Copy link" onClick={() => copy(it.payment_link)} className="p-1.5 rounded hover:bg-white/5" data-testid={`pl-copy-${it.invoice_no}`}>
                            <Copy size={14} className="text-white/60" />
                          </button>
                          <a href={it.payment_link} target="_blank" rel="noreferrer" title="Open" className="p-1.5 rounded hover:bg-white/5" data-testid={`pl-open-${it.invoice_no}`}>
                            <ExternalLink size={14} className="text-white/60" />
                          </a>
                        </>
                      )}
                      <a href={`/invoice/${it.invoice_no}`} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-[0.18em] text-[#fbce18] px-2 py-1" data-testid={`pl-invoice-${it.invoice_no}`}>Invoice</a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="w-full">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80">Create</div>
                <h3 className="font-display text-xl">New Payment Link</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/5" data-testid="pl-modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Customer Name" v={form.customer_name} on={(v) => setForm({ ...form, customer_name: v })} testid="pl-input-name" required />
              <F label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="pl-input-email" required />
              <F label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} testid="pl-input-phone" required />
              <F label="Amount" type="number" v={form.amount} on={(v) => setForm({ ...form, amount: v })} testid="pl-input-amount" required />
              <F label="Purpose" v={form.purpose} on={(v) => setForm({ ...form, purpose: v })} testid="pl-input-purpose" required />
              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Branch / Category</div>
                <input list="branch-list" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="pl-input-branch" required />
                <datalist id="branch-list">
                  {branches.map(b => (<option key={b.id} value={b.name} />))}
                </datalist>
              </label>
              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Currency</div>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="pl-input-currency">
                  {CURRENCIES.map(c => (<option key={c} value={c}>{c}</option>))}
                </select>
              </label>
              <F label="Expiry (days)" type="number" v={form.expiry_days} on={(v) => setForm({ ...form, expiry_days: v })} testid="pl-input-expiry" />
              <F label="Logo URL" v={form.logo_link} on={(v) => setForm({ ...form, logo_link: v })} testid="pl-input-logo" />
              <F label="Reference ID" v={form.reference_id} on={(v) => setForm({ ...form, reference_id: v })} testid="pl-input-ref" />
              <label className="block md:col-span-2">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Custom message</div>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="pl-input-message" />
              </label>
              <label className="block md:col-span-2">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Internal notes</div>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="pl-input-notes" />
              </label>
              <label className="flex items-center gap-2 text-sm text-white/70 md:col-span-2 mt-2">
                <input type="checkbox" checked={form.partial_payment} onChange={(e) => setForm({ ...form, partial_payment: e.target.checked })} className="accent-[#fbce18]" data-testid="pl-input-partial" />
                Allow partial payment
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm" data-testid="pl-cancel-btn">Cancel</button>
              <button type="submit" disabled={busy} className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="pl-submit-btn">
                {busy ? "Creating…" : "Create Link"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function F({ label, v, on, type = "text", testid, required }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">{label}{required && <span className="text-[#fbce18]">*</span>}</div>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} required={required} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid={testid} />
    </label>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="ldc-glass p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto ldc-scroll" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
